import type { MoneyContext } from "../money/MoneyContext";
import type { BetConfig, BetLockReason, BetStateSnapshot, BetSystem, MinorAmount } from "./BetSystemTypes";
import { resolveInitialBetStepIndex } from "./DefaultBetStepIndexResolver";

export class DefaultBetSystem implements BetSystem {
  private readonly betStepsMinor: MinorAmount[];
  private currentIndex: number;
  private readonly moneyContext: MoneyContext;

  /**
   * Display-only bet override. When non-null, takes precedence over the
   * ladder value for `getCurrentBetMinor()` / `getSnapshot()`. Set by replay
   * mode + pending-round restore to show the round's exact bet, regardless
   * of whether that value exists in the current operator's ladder. The
   * ladder index is preserved so clearing the override restores the
   * pre-override state.
   */
  private displayOverride: MinorAmount | null = null;

  private readonly listeners: Set<(s: BetStateSnapshot) => void> = new Set();

  /** The line counts offered and the one chosen (decision 56): none and 1 for a title without lines. */
  private readonly lineOptions: readonly number[];
  private lines: number;

  public constructor(input: {
    config: BetConfig;
    moneyContext: MoneyContext;
  }) {
    this.moneyContext = input.moneyContext;

    // A copy of the operator ladder; `GameInitialSettingsLoader` has refused an empty one and any step that is not whole minor units.
    this.betStepsMinor = [...input.config.betStepsMinor];

    const initialIndex = resolveInitialBetStepIndex({
      betStepsMinor: this.betStepsMinor,
      initialBetMinor: input.config.initialBetMinor,
    });

    this.currentIndex = clampIndex(initialIndex, this.betStepsMinor.length);

    this.lineOptions = [...(input.config.lineOptions ?? [])];
    const initialLines = input.config.initialLines;
    this.lines = initialLines !== undefined && this.lineOptions.includes(initialLines) ? initialLines : Math.max(1, ...this.lineOptions);
  }

  private locked: boolean = false;
  private reason: BetLockReason | null = null;

  public getBetStepsMinor(): readonly MinorAmount[] {
    return this.betStepsMinor;
  }

  public getSnapshot(): BetStateSnapshot {
    return {
      currentBetMinor: this.getCurrentBetMinor(),
      currentStepIndex: this.currentIndex,
      stepCount: this.betStepsMinor.length,
      lines: this.lines,
    };
  }

  public getCurrentBetMinor(): MinorAmount {
    return this.displayOverride ?? (BigInt(this.betStepsMinor[this.currentIndex]) * BigInt(this.lines)).toString();
  }

  public getLineOptions(): readonly number[] {
    return this.lineOptions;
  }

  public getLines(): number {
    return this.lines;
  }

  public setLines(lines: number): void {
    if (!this.lineOptions.includes(lines) || lines === this.lines) return;
    this.lines = lines;
    // An explicit choice, like a ladder change: a replay's or a restore's bet is not shown over it.
    this.displayOverride = null;
    this.emit();
  }

  public getLineBetMinor(): MinorAmount {
    return this.displayOverride === null ? this.betStepsMinor[this.currentIndex] : (BigInt(this.displayOverride) / BigInt(this.lines)).toString();
  }

  public setDisplayOverride(minor: MinorAmount | null, lines?: number): void {
    if (minor !== null && !isValidMinorIntString(minor)) {
      throw new Error(`BetSystem.setDisplayOverride: invalid minor amount "${minor}"`);
    }
    const linesChange = minor !== null && lines !== undefined && lines !== this.lines && this.lineOptions.includes(lines);
    if (this.displayOverride === minor && !linesChange) return;
    if (linesChange) this.lines = lines;
    this.displayOverride = minor;
    // Sync the ladder index when setting a non-null override. Without this, the displayed bet
    // (override) and the ladder index could diverge — e.g., pending-round restore sets override to
    // $100 while the ladder index stays at the boot default $1. A subsequent bet+ tap would call
    // setBetByIndex(currentIndex + 1) = $2, jumping the visible bet from $100 to $2. Syncing here
    // means +/- always advance from the displayed value. The override remains the source of truth
    // for off-ladder display until setBetByIndex (via +/- or setBetMinor) explicitly clears it.
    //
    // The step is taken AT OR BELOW the override, never above (user, 2026-09-21). Both callers hand
    // in a bet the player already played — a restored round or a replay — so the step this resolves
    // to becomes their next wager the moment the override is cleared, and rounding up would raise
    // it without them asking. Off-ladder overrides also make `+` move up to a real step rather than
    // skipping one.
    // With lines the override is their total (decision 56): the index syncs to a line's share of it.
    if (minor !== null) {
      this.currentIndex = resolveInitialBetStepIndex({
        betStepsMinor: this.betStepsMinor,
        initialBetMinor: this.getLineBetMinor(),
        rounding: "at_or_below",
      });
    }
    this.emit();
  }

  public setBetByIndex(index: number): void {
    const next = clampIndex(index, this.betStepsMinor.length);
    if (next === this.currentIndex) return;

    this.currentIndex = next;
    // An explicit user-driven (or programmatic) ladder change clears any
    // display override left over from replay / pending-round-restore.
    // Without this, bet changes update the index but the override keeps
    // returning the restored round's bet from getCurrentBetMinor(), so
    // UI taps appear to have no effect.
    this.displayOverride = null;
    this.emit();
  }

  public setBetMinor(minor: MinorAmount): void {
    if (!isValidMinorIntString(minor)) {
      throw new Error(`BetSystem.setBetMinor: invalid minor amount "${minor}"`);
    }

    const idx = this.betStepsMinor.indexOf(minor);
    if (idx >= 0) {
      this.setBetByIndex(idx);
      return;
    }

    // If not found, choose closest (keeps UX stable even for odd operator ladders)
    const closest = resolveInitialBetStepIndex({
      betStepsMinor: this.betStepsMinor,
      initialBetMinor: minor,
    });

    this.setBetByIndex(closest);
  }

  public increaseBet(): void {
    this.setBetByIndex(this.currentIndex + 1);
  }

  public decreaseBet(): void {
    this.setBetByIndex(this.currentIndex - 1);
  }

  public setMinBet(): void {
    this.setBetByIndex(0);
  }

  public setMaxBet(): void {
    this.setBetByIndex(this.betStepsMinor.length - 1);
  }

  public formatCurrentBet(): string {
    const minor = this.getCurrentBetMinor();

    return this.moneyContext.formatter.formatMinor(minor);
  }

  public isLocked(): boolean {
    return this.locked;
  }

  public getLockReason(): BetLockReason | null {
    return this.reason;
  }

  public lock(reason: BetLockReason): void {
    this.locked = true;
    this.reason = reason;
    this.emit();
  }

  public unlock(): void {
    this.locked = false;
    this.reason = null;
    this.emit();
  }

  public canIncrease(): boolean {
    return this.currentIndex < this.betStepsMinor.length - 1;
  }

  public canDecrease(): boolean {
    return this.currentIndex > 0;
  }

  public subscribe(listener: (snapshot: BetStateSnapshot) => void): () => void {
    this.listeners.add(listener);

    // Immediately push current state so UI can render without special-casing.
    listener(this.getSnapshot());

    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    const snap = this.getSnapshot();
    for (const fn of this.listeners) fn(snap);
  }
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  if (index < 0) return 0;
  if (index >= length) return length - 1;
  return index;
}

function isValidMinorIntString(value: string): boolean {
  if (typeof value !== "string" || value.length === 0) return false;
  // allow "0"
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    if (c < 48 || c > 57) return false;
  }
  return true;
}